import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bell, Check, CheckCheck, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Human-readable label for every kind emitted by the backend. Falls back to
// the raw kind string for anything unknown (safe default).
const KIND_LABEL = {
  assigned: "Ditugaskan",
  rescheduled: "Dijadwalkan Ulang",
  ticket_created: "Tiket Baru",
  ticket_assigned: "Ditugaskan",
  ticket_reassigned: "Dialihkan",
  ticket_unassigned: "PIC Dilepas",
  ticket_claimed: "Diklaim",
  ticket_status_changed: "Status Diubah",
  ticket_completed: "Selesai",
  ticket_cancelled: "Dibatalkan",
  ticket_deleted: "Tiket Dihapus",
  ticket_photo_uploaded: "Foto Diunggah",
  ticket_photo_deleted: "Foto Dihapus",
  report_approved: "Laporan Disetujui",
  report_rejected: "Laporan Ditolak",
  attendance_perlu_verifikasi: "Absensi Perlu Verifikasi",
  attendance_alpa_finalized: "Absensi ALPA Otomatis",
  attendance_daily_digest_koord: "Ringkasan Absensi Harian",
  attendance_request_approved: "Pengajuan Disetujui",
  attendance_request_rejected: "Pengajuan Ditolak",
  duty_assigned: "Ditugaskan DINAS",
  duty_updated: "DINAS Diperbarui",
  duty_unassigned: "Dilepas dari DINAS",
  overtime_perlu_verifikasi: "Lembur Perlu Verifikasi",
  auto_invoice_run_error: "Invoice Otomatis Gagal",
  auto_invoice_run_stuck: "Invoice Otomatis Macet",
  auto_invoice_not_run: "Invoice Otomatis Belum Berjalan",
};
const isAutoInvKind = (k) => typeof k === "string" && k.startsWith("auto_invoice_");

// Category grouping for the filter chips. Anything not matched falls into
// "tickets" so unknown/new ticket-scoped kinds keep the default bucket.
const CATEGORY_FOR_KIND = {
  ticket_photo_uploaded: "photos",
  ticket_photo_deleted: "photos",
  report_approved: "reports",
  report_rejected: "reports",
  attendance_perlu_verifikasi: "attendance",
  attendance_alpa_finalized: "attendance",
  attendance_daily_digest_koord: "attendance",
  attendance_request_approved: "attendance",
  attendance_request_rejected: "attendance",
  duty_assigned: "attendance",
  duty_updated: "attendance",
  duty_unassigned: "attendance",
  overtime_perlu_verifikasi: "attendance",
};
const categoryOf = (kind) => (isAutoInvKind(kind) ? "billing" : CATEGORY_FOR_KIND[kind] || "tickets");

const CATEGORIES = [
  { key: "all", label: "Semua" },
  { key: "tickets", label: "Tiket" },
  { key: "photos", label: "Foto" },
  { key: "reports", label: "Laporan" },
  { key: "attendance", label: "Absensi" },
  { key: "billing", label: "Billing" },
];

// Module-level set of notification IDs that have already produced a real-time
// toast. Shared across every NotificationBell instance (this component is
// mounted twice in Layout — one for desktop sidebar, one for mobile top bar).
// Without this de-dupe both instances would fire a toast on every new notif.
// A local set inside each instance would also lose state on StrictMode
// double-mount in dev.
const toastedIds = new Set();

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "baru saja";
  if (diff < 3600) return `${Math.floor(diff / 60)}m lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}j lalu`;
  return `${Math.floor(diff / 86400)}h lalu`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("all");
  const navigate = useNavigate();
  // Track popover open state via ref for use inside the (stable-closure)
  // load() callback — we suppress toasts while the user is actively viewing
  // the bell, since the new row is already visible in the popover.
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  const openTicketFromToast = (n) => {
    // Same wiring as NotificationBell row click so behavior stays consistent:
    // mark read → close popover (if any) → deep-link with optional tab param.
    if (!n) return;
    if (isAutoInvKind(n.kind)) {
      if (!n.read) markOne(n.id);
      setOpen(false);
      navigate("/billing/invoices?autoinv=1");
      return;
    }
    // Fase 4.5 — Overtime PERLU_VERIFIKASI notification deep-links to
    // /overtime?id=<overtime_id> where Overtime.jsx auto-opens the Detail
    // dialog. Backend GET /overtime/{id} enforces location scope so an
    // outsider deep-link fails silently on the client (toast error, no
    // data leak).
    if (n.kind === "overtime_perlu_verifikasi") {
      if (!n.read) markOne(n.id);
      setOpen(false);
      const oid = encodeURIComponent(n.overtime_id || "");
      if (!oid) return;
      navigate(`/overtime?id=${oid}`);
      return;
    }
    // Attendance verification & auto-ALPA alerts jump straight to Rekap
    // Absensi with filters pre-applied so the admin/koord lands on the
    // exact row.
    if (n.kind === "attendance_perlu_verifikasi" || n.kind === "attendance_alpa_finalized" || n.kind === "attendance_daily_digest_koord") {
      if (!n.read) markOne(n.id);
      setOpen(false);
      const p = new URLSearchParams();
      if (n.kind === "attendance_perlu_verifikasi") p.set("status", "PERLU_VERIFIKASI");
      if (n.kind === "attendance_alpa_finalized") p.set("status", "ALPA");
      if (n.attendance_user_id) p.set("user_id", n.attendance_user_id);
      const dateKey = n.attendance_date || n.digest_date;
      if (dateKey) {
        p.set("start_date", dateKey);
        p.set("end_date", dateKey);
      }
      navigate(`/attendance-report?${p.toString()}`);
      return;
    }
    // Feb 2026 — Attendance request approve/reject notif jump ke
    // halaman Pengajuan Absensi supaya user (teknisi) dapat langsung
    // melihat detail pengajuan yang diproses.
    if (n.kind === "attendance_request_approved" || n.kind === "attendance_request_rejected") {
      if (!n.read) markOne(n.id);
      setOpen(false);
      const p = new URLSearchParams();
      if (n.attendance_request_id) p.set("open", n.attendance_request_id);
      navigate(`/attendance-requests${p.toString() ? `?${p}` : ""}`);
      return;
    }
    // Feb 2026 — DINAS assignment notif → halaman Dinas supaya teknisi
    // bisa langsung melihat target GPS dan detail penugasan.
    if (n.kind === "duty_assigned" || n.kind === "duty_updated" || n.kind === "duty_unassigned") {
      if (!n.read) markOne(n.id);
      setOpen(false);
      navigate("/attendance-duties");
      return;
    }
    if (!n.ticket_id) return;
    if (!n.read) markOne(n.id);
    setOpen(false);
    const targetTab = (n.kind === "ticket_photo_uploaded" || n.kind === "ticket_photo_deleted")
      ? "photos"
      : null;
    const tid = encodeURIComponent(n.ticket_id);
    navigate(targetTab ? `/tickets?open=${tid}&tab=${targetTab}` : `/tickets?open=${tid}`);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const nextItems = data.items || [];
      // On first fetch (module-level toastedIds empty), seed silently so we
      // don't spam historical notifications. Subsequent polls diff against
      // toastedIds — both NotificationBell instances share this Set so a
      // single new notification produces exactly one toast.
      const isFirstLoad = toastedIds.size === 0;
      if (isFirstLoad) {
        nextItems.forEach((n) => toastedIds.add(n.id));
      } else {
        const fresh = nextItems.filter((n) => !toastedIds.has(n.id));
        if (fresh.length > 0 && !openRef.current) {
          if (fresh.length <= 3) {
            fresh.forEach((n) => {
              const isAttendance = n.kind === "attendance_perlu_verifikasi" || n.kind === "attendance_alpa_finalized" || n.kind === "attendance_daily_digest_koord" || n.kind === "attendance_request_approved" || n.kind === "attendance_request_rejected" || n.kind === "duty_assigned" || n.kind === "duty_updated" || n.kind === "duty_unassigned";
              const isOvertime = n.kind === "overtime_perlu_verifikasi";
              const isAutoInv = isAutoInvKind(n.kind);
              const toastFn = (isAttendance || isOvertime || isAutoInv) ? toast.warning : toast;
              const canOpen = isAttendance || isOvertime || isAutoInv || !!n.ticket_id;
              toastFn(KIND_LABEL[n.kind] || "Notifikasi", {
                description: n.message || "",
                duration: 6000,
                action: canOpen
                  ? { label: "Buka", onClick: () => openTicketFromToast(n) }
                  : undefined,
              });
            });
          } else {
            toast(`${fresh.length} notifikasi baru`, {
              description: "Klik lonceng untuk melihat detail.",
              duration: 6000,
              action: { label: "Lihat", onClick: () => setOpen(true) },
            });
          }
        }
        fresh.forEach((n) => toastedIds.add(n.id));
      }
      setItems(nextItems);
      setUnread(data.unread || 0);
    } catch (e) {
      console.error("[NotificationBell] fetch failed:", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markAll = async () => {
    setLoading(true);
    try {
      await api.post("/notifications/read-all");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="btn-notif"
          className="relative h-9 w-9 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Notifikasi"
        >
          <Bell className="h-4 w-4" />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                data-testid="notif-badge"
                className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#EA580C] text-white text-[9px] font-bold flex items-center justify-center"
              >
                {unread > 9 ? "9+" : unread}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 border-[#E5E7EB]" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Notifikasi</div>
            {unread > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-[10px] font-bold">
                {unread} baru
              </span>
            )}
          </div>
          <button
            onClick={markAll}
            disabled={loading || unread === 0}
            className="text-[11px] font-semibold text-[#0055FF] hover:underline disabled:opacity-40 flex items-center gap-1"
            data-testid="btn-notif-read-all"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
            Tandai semua
          </button>
        </div>
        {/* Category filter chips — client-side filter only. Server-side
            pagination/mark-all are untouched. Counts are derived from the
            currently-loaded `items` batch (same source as the list below). */}
        <div className="flex items-center gap-1.5 px-4 py-2 border-b border-[#F3F4F6] overflow-x-auto no-scrollbar" data-testid="notif-filter-chips">
          {CATEGORIES.map((c) => {
            const count = c.key === "all"
              ? items.length
              : items.filter((n) => categoryOf(n.kind) === c.key).length;
            const active = category === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                data-testid={`notif-filter-${c.key}`}
                data-active={active ? "true" : "false"}
                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? "bg-[#0055FF] text-white"
                    : "bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]"
                }`}
              >
                {c.label}
                <span className={`text-[10px] font-mono-tab ${active ? "text-white/80" : "text-[#9CA3AF]"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {(() => {
            const visible = category === "all"
              ? items
              : items.filter((n) => categoryOf(n.kind) === category);
            if (items.length === 0) {
              return (
                <div className="px-6 py-10 text-center text-sm text-[#9CA3AF]">
                  Belum ada notifikasi.
                </div>
              );
            }
            if (visible.length === 0) {
              return (
                <div
                  className="px-6 py-10 text-center text-sm text-[#9CA3AF]"
                  data-testid="notif-empty-filter"
                >
                  Tidak ada notifikasi di kategori ini.
                </div>
              );
            }
            return visible.map((n) => {
            const kindLabel = KIND_LABEL[n.kind] || n.kind;
            // Attendance verification alerts land on the Rekap Absensi page.
            // Ticket-scoped rows only navigate when we have a ticket_id (bulk
            // delete summary rows have ticket_id=null → not clickable).
            const isAttendance = n.kind === "attendance_perlu_verifikasi" || n.kind === "attendance_alpa_finalized" || n.kind === "attendance_daily_digest_koord" || n.kind === "attendance_request_approved" || n.kind === "attendance_request_rejected" || n.kind === "duty_assigned" || n.kind === "duty_updated" || n.kind === "duty_unassigned";
            const isOvertime = n.kind === "overtime_perlu_verifikasi";
            const isAutoInv = isAutoInvKind(n.kind);
            const canOpen = isAttendance || isOvertime || isAutoInv || !!n.ticket_id;
            // Photo notifications land directly on the "Foto Bukti" tab so
            // admins/technicians see what changed without an extra click.
            // History-only kinds could reuse the same mechanism (?tab=history)
            // in the future; today we only special-case photos per user need.
            const targetTab = (n.kind === "ticket_photo_uploaded" || n.kind === "ticket_photo_deleted")
              ? "photos"
              : null;
            const handleOpen = () => {
              if (!canOpen) return;
              if (isAttendance || isOvertime || isAutoInv) {
                openTicketFromToast(n);
                return;
              }
              if (!n.read) markOne(n.id);
              setOpen(false);
              const tid = encodeURIComponent(n.ticket_id);
              const url = targetTab ? `/tickets?open=${tid}&tab=${targetTab}` : `/tickets?open=${tid}`;
              navigate(url);
            };
            return (
            <div
              key={n.id}
              onClick={handleOpen}
              role={canOpen ? "button" : undefined}
              tabIndex={canOpen ? 0 : undefined}
              onKeyDown={canOpen ? (e) => { if (e.key === "Enter") handleOpen(); } : undefined}
              className={`px-4 py-3 border-b border-[#F3F4F6] flex items-start gap-3 ${
                n.read ? "" : "bg-[#0055FF]/[0.04]"
              } ${canOpen ? "cursor-pointer hover:bg-[#F8F9FA]" : "cursor-default"}`}
              data-testid={`notif-${n.id}`}
              data-clickable={canOpen ? "true" : "false"}
            >
              <div
                className={`h-2 w-2 rounded-full mt-1.5 flex-shrink-0 ${
                  n.read ? "bg-transparent" : "bg-[#0055FF]"
                }`}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                  {kindLabel}
                </div>
                <div className="text-sm text-[#0A0A0A] mt-0.5 break-words">{n.message}</div>
                <div className="text-[10px] text-[#9CA3AF] mt-1 font-mono-tab">{timeAgo(n.created_at)}</div>
              </div>
              {!n.read && (
                <button
                  onClick={(e) => { e.stopPropagation(); markOne(n.id); }}
                  className="h-6 w-6 rounded-md hover:bg-white flex items-center justify-center"
                  title="Tandai dibaca"
                  data-testid={`notif-mark-read-${n.id}`}
                >
                  <Check className="h-3.5 w-3.5 text-[#4B5563]" />
                </button>
              )}
            </div>
            );
          });
          })()}
        </div>
      </PopoverContent>
    </Popover>
  );
}
